import { useCallback } from 'react';
import { create } from 'zustand';

import { CurrentUser, RoleType } from './SecurityTypes';

interface AppState {
  currentUser: CurrentUser | null;
  setCurrentUser: (user: CurrentUser) => void;
  isAuthenticated: boolean;
}

export const useCurrentUserStore = create<AppState>()(set => ({
  currentUser: null,
  setCurrentUser: (currentUser: CurrentUser | null) => set({ currentUser, isAuthenticated: currentUser != null }),
  isAuthenticated: false,
}));

export function useCurrentUser() {
  return useCurrentUserStore(state => {
    const user = state.currentUser;
    if (user == null) {
      throw new Error("Invariant violated: watching current user when it's null");
    }

    return user;
  });
}

export function useRoleBoundary() {
  const user = useCurrentUser();

  const isRole = useCallback(
    (expectedRoleType: RoleType) => {
      return user.currentRole === expectedRoleType;
    },
    [user],
  );

  const isRoleOneOf = useCallback(
    (expectedRoleTypes: RoleType[]) => {
      return expectedRoleTypes.indexOf(user.currentRole) !== -1;
    },
    [user],
  );

  return {
    roleType: user.currentRole,
    isRoleOneOf,
    isRole,
  };
}
